import { useEffect } from 'react';
import { Outlet, useMatch, useNavigate, useParams } from 'react-router';
import {
    OnboardingStepType,
    useOnboarding,
} from '../routes/authorized/onboarding/OnboardingConfig';
import {
    LoadingSpinnerPresets,
    PresetLoadingSpinner,
} from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner';
import { useParkingaboAuthedPathGeneration } from '../routes/RouteUtils';

import { useDocumentVisibilityEffect } from 'dg-web-shared/common/hooks/useDocumentVisibility';
import { useCustomerContext } from './CustomerProvider.tsx';
import { ParkingaboCustomerPaymentMode } from 'product-shared/parkingabo/ParkingaboOperatingMode.tsx';

export function OnboardingRequiredChecker() {
    const navigate = useNavigate();
    const { user, refetchUser } = useCustomerContext();
    const generateAuthedParkingaboPath = useParkingaboAuthedPathGeneration();
    const config = useOnboarding();
    const firstIncompleteOnboardingStep = config.steps.find(
        step => !step.completed,
    );
    const isConfigLoaded = !!config.steps.find(
        step => step.completed !== undefined,
    );
    const { operatorExternalId } = useParams<{ operatorExternalId?: string }>();
    const onboardingSuccess =
        useMatch(generateAuthedParkingaboPath('onboarding/success')) !== null;
    const onboardingComplete = user.onboardingCompleteTime !== null;
    const isReady = operatorExternalId ? isConfigLoaded : true;

    useDocumentVisibilityEffect(visibilityState => {
        if (visibilityState === 'visible') {
            refetchUser();
        }
    });

    useEffect(() => {
        if (!operatorExternalId || !isConfigLoaded) {
            return;
        }
        if (!onboardingSuccess && !onboardingComplete) {
            if (firstIncompleteOnboardingStep) {
                navigate(
                    getRedirectPathForAppModeAndOnboardingStep(
                        generateAuthedParkingaboPath,
                        user.role.paymentMode,
                        firstIncompleteOnboardingStep.type,
                    ),
                );
            }
        }
    }, [
        firstIncompleteOnboardingStep,
        generateAuthedParkingaboPath,
        isConfigLoaded,
        navigate,
        onboardingComplete,
        onboardingSuccess,
        user.role.paymentMode,
        operatorExternalId,
    ]);

    if (!isReady) {
        return (
            <PresetLoadingSpinner
                preset={LoadingSpinnerPresets.FillAllSpaceAndCenter}
            />
        );
    } else {
        return <Outlet />;
    }
}

function getRedirectPathForAppModeAndOnboardingStep(
    generateAuthedParkingaboPath: (path: string) => string,
    paymentMode: ParkingaboCustomerPaymentMode,
    step: OnboardingStepType,
): string {
    switch (paymentMode) {
        case ParkingaboCustomerPaymentMode.APP_PAYMENT:
            return generateAuthedParkingaboPath(
                getSignupByEmailDomainPathForOnboardingStep(step),
            );
        case ParkingaboCustomerPaymentMode.INDIVIDUAL_PAYMENT:
        case ParkingaboCustomerPaymentMode.COUNTER:
            return '/not-found';
    }
}

function getSignupByEmailDomainPathForOnboardingStep(
    step: OnboardingStepType,
): string {
    switch (step) {
        case OnboardingStepType.USER_DATA:
            return 'onboarding/user-data';
        case OnboardingStepType.VEHICLE:
            return 'onboarding/vehicle';
        case OnboardingStepType.PAYMENT:
            return 'onboarding/payment';
    }
}
